/**
 * CustomerModal Component
 * Multi-step form for adding/editing individual taxpayer customers
 */

import { useState, useEffect } from 'react'
import { X, ChevronLeft, ChevronRight, Check, AlertCircle } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getDocumentService } from '@/services/documents'
import type { DocumentCategory } from '@/types/document.types'
import { useCustomerStore } from '@/store/customerStore'
import { toast } from '@/store/toastStore'
import type {
  CreateCustomerInput,
  Address,
  Gender,
  Occupation,
  IncomeRange,
  IndividualCustomer,
} from '@/types/customer'
import {
  PAN_REGEX,
  MOBILE_REGEX,
  EMAIL_REGEX,
  PINCODE_REGEX,
  INDIAN_STATES,
} from '@/types/customer'

interface CustomerModalProps {
  isOpen: boolean
  onClose: () => void
  customer?: IndividualCustomer | null
  onSuccess?: (customerId: string) => void
}

type FormStep = 1 | 2 | 3 | 4 | 5 | 6

interface FormData {
  // Step 1: Basic Info
  fullName: string
  pan: string
  dateOfBirth: string
  gender: Gender

  // Step 2: Contact
  emailPrimary: string
  emailSecondary: string
  mobilePrimary: string
  mobileSecondary: string
  whatsappNumber: string

  // Step 3: Address
  residentialAddress: Address
  permanentAddressSame: boolean
  permanentAddress: Address
  correspondenceAddress: Address
  hasCorrespondenceAddress: boolean

  // Step 4: Financial Profile
  occupation: Occupation
  employerName: string
  annualIncomeRange: IncomeRange
  hasHouseProperty: boolean
  hasCapitalGains: boolean
  hasBusinessIncome: boolean
  previousYearTaxPaid: string
}

interface FormErrors {
  [key: string]: string
}

const emptyAddress: Address = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  pincode: '',
  country: 'India',
}

const initialFormData: FormData = {
  fullName: '',
  pan: '',
  dateOfBirth: '',
  gender: 'male',
  emailPrimary: '',
  emailSecondary: '',
  mobilePrimary: '',
  mobileSecondary: '',
  whatsappNumber: '',
  residentialAddress: { ...emptyAddress },
  permanentAddressSame: true,
  permanentAddress: { ...emptyAddress },
  correspondenceAddress: { ...emptyAddress },
  hasCorrespondenceAddress: false,
  occupation: 'salaried',
  employerName: '',
  annualIncomeRange: '5-10L',
  hasHouseProperty: false,
  hasCapitalGains: false,
  hasBusinessIncome: false,
  previousYearTaxPaid: '',
}

export default function CustomerModal({
  isOpen,
  onClose,
  customer,
  onSuccess,
}: CustomerModalProps) {
  const { addCustomer, updateCustomer } = useCustomerStore()
  const [step, setStep] = useState<FormStep>(1)
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set())

  const { hasPermission, canAccessFeature } = useAuth()
  const canUpload = hasPermission('txh.document.upload') && canAccessFeature('documents')
  const [personalDocuments, setPersonalDocuments] = useState<
    Array<{ category: DocumentCategory; file: File }>
  >([])
  const [savedCustomerId, setSavedCustomerId] = useState<string | null>(null)
  const isEditMode = !!customer

  // Reset form when modal opens in create mode
  useEffect(() => {
    if (!isEditMode && isOpen) {
      setFormData(initialFormData)
      setStep(1)
      setCompletedSteps(new Set())
      setErrors({})
      setTouched(new Set())
      setSubmitError(null)
    }
  }, [isOpen, isEditMode])

  // Load customer data if editing
  useEffect(() => {
    if (customer && isOpen) {
      setFormData({
        fullName: customer.fullName,
        pan: customer.pan,
        dateOfBirth: customer.dateOfBirth || '',
        gender: customer.gender || 'other',
        emailPrimary: customer.emailPrimary,
        emailSecondary: customer.emailSecondary || '',
        mobilePrimary: customer.mobilePrimary,
        mobileSecondary: customer.mobileSecondary || '',
        whatsappNumber: customer.whatsappNumber || '',
        residentialAddress: { ...emptyAddress, ...customer.residentialAddress },
        permanentAddressSame: !customer.permanentAddress,
        permanentAddress: { ...emptyAddress, ...customer.permanentAddress },
        correspondenceAddress: { ...emptyAddress, ...customer.correspondenceAddress },
        hasCorrespondenceAddress: !!customer.correspondenceAddress,
        occupation: customer.occupation || 'other',
        employerName: customer.employerName || '',
        annualIncomeRange: customer.annualIncomeRange || '0-5L',
        hasHouseProperty: customer.hasHouseProperty,
        hasCapitalGains: customer.hasCapitalGains,
        hasBusinessIncome: customer.hasBusinessIncome,
        previousYearTaxPaid: customer.previousYearTaxPaid?.toString() || '',
      })
    }
  }, [customer, isOpen])

  const getStepErrors = (currentStep: FormStep): FormErrors => {
    const newErrors: FormErrors = {}

    switch (currentStep) {
      case 1:
        if (!formData.fullName.trim()) {
          newErrors.fullName = 'Full name is required'
        }
        if (!formData.pan.trim()) {
          newErrors.pan = 'PAN is required'
        } else if (!PAN_REGEX.test(formData.pan.toUpperCase())) {
          newErrors.pan = 'Invalid PAN format (e.g., ABCDE1234F)'
        }
        if (!formData.dateOfBirth) {
          newErrors.dateOfBirth = 'Date of birth is required'
        }
        break

      case 2:
        if (!formData.emailPrimary.trim()) {
          newErrors.emailPrimary = 'Primary email is required'
        } else if (!EMAIL_REGEX.test(formData.emailPrimary)) {
          newErrors.emailPrimary = 'Invalid email format'
        }
        if (formData.emailSecondary && !EMAIL_REGEX.test(formData.emailSecondary)) {
          newErrors.emailSecondary = 'Invalid email format'
        }
        if (!formData.mobilePrimary.trim()) {
          newErrors.mobilePrimary = 'Primary mobile is required'
        } else if (!MOBILE_REGEX.test(formData.mobilePrimary.replace(/\D/g, ''))) {
          newErrors.mobilePrimary = 'Invalid mobile number (10 digits required)'
        }
        if (
          formData.mobileSecondary &&
          !MOBILE_REGEX.test(formData.mobileSecondary.replace(/\D/g, ''))
        ) {
          newErrors.mobileSecondary = 'Invalid mobile number'
        }
        break

      case 3:
        if (!formData.residentialAddress.line1.trim()) {
          newErrors['residentialAddress.line1'] = 'Address line 1 is required'
        }
        if (!formData.residentialAddress.city.trim()) {
          newErrors['residentialAddress.city'] = 'City is required'
        }
        if (!formData.residentialAddress.state) {
          newErrors['residentialAddress.state'] = 'State is required'
        }
        if (!formData.residentialAddress.pincode.trim()) {
          newErrors['residentialAddress.pincode'] = 'Pincode is required'
        } else if (!PINCODE_REGEX.test(formData.residentialAddress.pincode)) {
          newErrors['residentialAddress.pincode'] = 'Invalid pincode (6 digits required)'
        }
        break

      case 4:
        // No mandatory fields in financial profile
        break
    }

    return newErrors
  }

  const validateStep = (currentStep: FormStep): boolean => {
    const newErrors = getStepErrors(currentStep)
    setErrors(newErrors)
    setTouched(previous => new Set([...previous, ...Object.keys(newErrors)]))
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(step)) {
      setCompletedSteps(prev => new Set(prev).add(step))
      setStep(prev => Math.min(prev + 1, 6) as FormStep)
    }
  }

  const handleBack = () => {
    setStep(prev => Math.max(prev - 1, 1) as FormStep)
  }

  const handleSubmit = async () => {
    const stepErrors = ([1, 2, 3, 4] as FormStep[]).map(candidate => ({
      step: candidate,
      errors: getStepErrors(candidate),
    }))
    const allErrors = Object.assign({}, ...stepErrors.map(entry => entry.errors))
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors)
      setTouched(previous => new Set([...previous, ...Object.keys(allErrors)]))
      setStep(stepErrors.find(entry => Object.keys(entry.errors).length > 0)?.step ?? 1)
      setSubmitError('Please correct the highlighted fields before saving.')
      return
    }

    const input: CreateCustomerInput = {
      fullName: formData.fullName.trim(),
      pan: formData.pan.toUpperCase(),
      dateOfBirth: formData.dateOfBirth,
      gender: formData.gender,
      emailPrimary: formData.emailPrimary.trim().toLowerCase(),
      emailSecondary: formData.emailSecondary.trim().toLowerCase() || undefined,
      mobilePrimary: formData.mobilePrimary.replace(/\D/g, ''),
      mobileSecondary: formData.mobileSecondary.replace(/\D/g, '') || undefined,
      whatsappNumber: formData.whatsappNumber.replace(/\D/g, '') || undefined,
      residentialAddress: formData.residentialAddress,
      permanentAddress: formData.permanentAddressSame ? undefined : formData.permanentAddress,
      correspondenceAddress: formData.hasCorrespondenceAddress
        ? formData.correspondenceAddress
        : undefined,
      occupation: formData.occupation,
      employerName: formData.employerName.trim() || undefined,
      annualIncomeRange: formData.annualIncomeRange,
      hasHouseProperty: formData.hasHouseProperty,
      hasCapitalGains: formData.hasCapitalGains,
      hasBusinessIncome: formData.hasBusinessIncome,
      previousYearTaxPaid: formData.previousYearTaxPaid
        ? parseInt(formData.previousYearTaxPaid)
        : undefined,
    }

    setIsSubmitting(true)
    setSubmitError(null)
    try {
      let targetId = savedCustomerId
      if (!targetId) {
        if (isEditMode && customer) {
          await updateCustomer(customer.id, input)
          targetId = customer.id
        } else {
          targetId = (await addCustomer(input)).id
        }
        setSavedCustomerId(targetId)
      }
      for (const document of personalDocuments) {
        await getDocumentService().upload({
          file: document.file,
          name: document.file.name,
          category: document.category,
          linkedEntityType: 'customer',
          linkedEntityId: targetId,
        })
        setPersonalDocuments(previous => previous.filter(item => item !== document))
      }
      onSuccess?.(targetId)
      toast.success(
        isEditMode ? 'Customer updated' : 'Customer created',
        `${input.fullName}'s details have been saved.`
      )
      handleClose()
    } catch (err) {
      console.error('Error saving customer:', err)
      const message = err instanceof Error ? err.message : 'Please try again.'
      setSubmitError(message)
      toast.error('Could not save customer', message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setStep(1)
    setCompletedSteps(new Set())
    setFormData(initialFormData)
    setErrors({})
    setTouched(new Set())
    setSubmitError(null)
    setIsSubmitting(false)
    setPersonalDocuments([])
    setSavedCustomerId(null)
    onClose()
  }

  const handleBlur = (field: string) => {
    setTouched(prev => new Set(prev).add(field))
    setErrors(getStepErrors(step))
  }

  const updateField = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setErrors(previous => {
      const next = { ...previous }
      delete next[field]
      return next
    })
    setSubmitError(null)
  }

  const updateAddressField = (
    addressType: 'residentialAddress' | 'permanentAddress' | 'correspondenceAddress',
    field: keyof Address,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [addressType]: { ...prev[addressType], [field]: value },
    }))
    const errorKey = `${addressType}.${field}`
    setErrors(previous => {
      const next = { ...previous }
      delete next[errorKey]
      return next
    })
    setSubmitError(null)
  }

  const getError = (field: string) => {
    return touched.has(field) ? errors[field] : undefined
  }

  if (!isOpen) return null

  const steps = [
    { number: 1, title: 'Basic Info' },
    { number: 2, title: 'Contact' },
    { number: 3, title: 'Address' },
    { number: 4, title: 'Financial' },
    { number: 5, title: 'Documents' },
    { number: 6, title: 'Review' },
  ]

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-form-title"
    >
      <div className="bg-white dark:bg-surface-dark rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-overlay">
          <h2 id="customer-form-title" className="text-xl font-semibold text-text-primary">
            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
          </h2>
          <button
            onClick={handleClose}
            aria-label="Close customer form"
            className="btn btn-ghost btn-circle btn-sm"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-surface-overlay">
          <div className="flex items-center justify-between">
            {steps.map((s, index) => {
              const isCompleted = completedSteps.has(s.number) && step !== s.number
              const isCurrent = step === s.number
              return (
                <div key={s.number} className="flex items-center">
                  <div
                    className={`flex flex-col items-center ${
                      s.number < step || completedSteps.has(s.number) ? 'cursor-pointer' : ''
                    }`}
                    onClick={() => {
                      if (s.number < step || completedSteps.has(s.number)) {
                        setStep(s.number as FormStep)
                      }
                    }}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                        isCompleted
                          ? 'bg-green-500 text-white'
                          : isCurrent
                            ? 'bg-primary-600 text-white'
                            : 'bg-surface-overlay text-text-secondary'
                      }`}
                    >
                      {isCompleted ? <Check className="w-4 h-4" /> : s.number}
                    </div>
                    <span
                      className={`text-xs mt-1 hidden sm:block ${
                        isCompleted || isCurrent
                          ? 'text-text-primary font-medium'
                          : 'text-text-tertiary'
                      }`}
                    >
                      {s.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-4 sm:w-8 h-0.5 mx-1 sm:mx-2 ${
                        completedSteps.has(s.number) ? 'bg-green-500' : 'bg-surface-overlay'
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {submitError && (
            <div
              role="alert"
              className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400 flex items-start gap-2"
            >
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{submitError} You can update the form and submit again.</span>
            </div>
          )}
          <div key={step} className="animate-in fade-in duration-150">
            {savedCustomerId && (
              <p role="status" className="mb-4 text-sm text-text-secondary">
                Customer saved. Retry to upload remaining documents; the customer will not be
                created again.
              </p>
            )}
            {step === 5 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-text-primary">Personal Documents</h3>
                <p className="text-sm text-text-secondary">
                  Add PAN, Aadhaar and other identity documents now, or upload them later from the
                  customer's Documents tab.
                </p>
                {canUpload ? (
                  ['pan_card', 'aadhaar_card', 'passport'].map(category => (
                    <label key={category} className="block text-sm font-medium text-text-primary">
                      {category === 'pan_card'
                        ? 'PAN Card'
                        : category === 'aadhaar_card'
                          ? 'Aadhaar Card'
                          : 'Passport'}{' '}
                      <span className="text-text-secondary">(optional)</span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        className="input input-bordered mt-1 w-full"
                        aria-label={
                          category === 'pan_card'
                            ? 'PAN Card document'
                            : category === 'aadhaar_card'
                              ? 'Aadhaar Card document'
                              : 'Passport document'
                        }
                        disabled={isSubmitting}
                        onChange={event => {
                          const file = event.target.files?.[0]
                          setPersonalDocuments(previous => [
                            ...previous.filter(item => item.category !== category),
                            ...(file ? [{ category, file }] : []),
                          ])
                        }}
                      />
                    </label>
                  ))
                ) : (
                  <p className="text-sm text-text-secondary">
                    Document upload requires document access for your account and plan.
                  </p>
                )}
                {personalDocuments.map(document => (
                  <p key={document.category} className="text-sm text-text-secondary">
                    {document.file.name}
                  </p>
                ))}
              </div>
            )}
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={e => updateField('fullName', e.target.value)}
                    onBlur={() => handleBlur('fullName')}
                    placeholder="Enter full name as per PAN"
                    className={`input input-bordered w-full ${getError('fullName') ? 'border-red-500' : ''}`}
                  />
                  {getError('fullName') && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {getError('fullName')}
                    </p>
                  )}
                  {!getError('fullName') && (
                    <p className="text-xs text-text-tertiary mt-1">
                      Enter the full name exactly as it appears on the PAN card.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    PAN <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.pan}
                    onChange={e => updateField('pan', e.target.value.toUpperCase())}
                    onBlur={() => handleBlur('pan')}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    className={`input input-bordered w-full font-mono uppercase ${getError('pan') ? 'border-red-500' : ''}`}
                  />
                  {getError('pan') && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {getError('pan')}
                    </p>
                  )}
                  {!getError('pan') && (
                    <p className="text-xs text-text-tertiary mt-1">
                      Use the 10-character PAN format, for example ABCDE1234F.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={e => updateField('dateOfBirth', e.target.value)}
                      onBlur={() => handleBlur('dateOfBirth')}
                      className={`input input-bordered w-full ${getError('dateOfBirth') ? 'border-red-500' : ''}`}
                    />
                    {getError('dateOfBirth') && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {getError('dateOfBirth')}
                      </p>
                    )}
                    {!getError('dateOfBirth') && (
                      <p className="text-xs text-text-tertiary mt-1">
                        Date of birth as per PAN/Aadhaar records.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.gender}
                      onChange={e => updateField('gender', e.target.value as Gender)}
                      className="input input-bordered w-full"
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Contact Information */}
            {step === 2 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Primary Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.emailPrimary}
                    onChange={e => updateField('emailPrimary', e.target.value)}
                    onBlur={() => handleBlur('emailPrimary')}
                    placeholder="email@example.com"
                    className={`input input-bordered w-full ${getError('emailPrimary') ? 'border-red-500' : ''}`}
                  />
                  {getError('emailPrimary') && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {getError('emailPrimary')}
                    </p>
                  )}
                  {!getError('emailPrimary') && (
                    <p className="text-xs text-text-tertiary mt-1">
                      Enter an address that can receive customer notifications.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Secondary Email
                  </label>
                  <input
                    type="email"
                    value={formData.emailSecondary}
                    onChange={e => updateField('emailSecondary', e.target.value)}
                    onBlur={() => handleBlur('emailSecondary')}
                    placeholder="alternate@example.com"
                    className={`input input-bordered w-full ${getError('emailSecondary') ? 'border-red-500' : ''}`}
                  />
                  {getError('emailSecondary') && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" /> {getError('emailSecondary')}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Primary Mobile <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.mobilePrimary}
                      onChange={e => updateField('mobilePrimary', e.target.value)}
                      onBlur={() => handleBlur('mobilePrimary')}
                      placeholder="9876543210"
                      maxLength={10}
                      className={`input input-bordered w-full ${getError('mobilePrimary') ? 'border-red-500' : ''}`}
                    />
                    {getError('mobilePrimary') && (
                      <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {getError('mobilePrimary')}
                      </p>
                    )}
                    {!getError('mobilePrimary') && (
                      <p className="text-xs text-text-tertiary mt-1">
                        Enter a 10-digit Indian mobile number.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Secondary Mobile
                    </label>
                    <input
                      type="tel"
                      value={formData.mobileSecondary}
                      onChange={e => updateField('mobileSecondary', e.target.value)}
                      placeholder="Optional"
                      maxLength={10}
                      onBlur={() => handleBlur('mobileSecondary')}
                      className={`input input-bordered w-full ${getError('mobileSecondary') ? 'border-red-500' : ''}`}
                    />
                    {getError('mobileSecondary') && (
                      <p className="text-red-500 text-sm mt-1">{getError('mobileSecondary')}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    WhatsApp Number
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="tel"
                      value={formData.whatsappNumber}
                      onChange={e => updateField('whatsappNumber', e.target.value)}
                      placeholder="Same as primary mobile"
                      maxLength={10}
                      className="input input-bordered flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => updateField('whatsappNumber', formData.mobilePrimary)}
                      className="btn btn-outline btn-sm"
                    >
                      Copy Primary
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Address */}
            {step === 3 && (
              <div className="space-y-6">
                <div>
                  <h3 className="font-medium text-text-primary mb-3">Residential Address</h3>
                  <div className="space-y-3">
                    <div>
                      <input
                        type="text"
                        value={formData.residentialAddress.line1}
                        onChange={e =>
                          updateAddressField('residentialAddress', 'line1', e.target.value)
                        }
                        onBlur={() => handleBlur('residentialAddress.line1')}
                        placeholder="Address Line 1 *"
                        className={`input input-bordered w-full ${getError('residentialAddress.line1') ? 'border-red-500' : ''}`}
                      />
                      {getError('residentialAddress.line1') && (
                        <p className="text-red-500 text-sm mt-1">
                          {getError('residentialAddress.line1')}
                        </p>
                      )}
                    </div>
                    <input
                      type="text"
                      value={formData.residentialAddress.line2 || ''}
                      onChange={e =>
                        updateAddressField('residentialAddress', 'line2', e.target.value)
                      }
                      placeholder="Address Line 2"
                      className="input input-bordered w-full"
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          value={formData.residentialAddress.city}
                          onChange={e =>
                            updateAddressField('residentialAddress', 'city', e.target.value)
                          }
                          onBlur={() => handleBlur('residentialAddress.city')}
                          placeholder="City *"
                          className={`input input-bordered w-full ${getError('residentialAddress.city') ? 'border-red-500' : ''}`}
                        />
                        {getError('residentialAddress.city') && (
                          <p className="text-red-500 text-sm mt-1">
                            {getError('residentialAddress.city')}
                          </p>
                        )}
                      </div>
                      <div>
                        <select
                          value={formData.residentialAddress.state}
                          onChange={e =>
                            updateAddressField('residentialAddress', 'state', e.target.value)
                          }
                          onBlur={() => handleBlur('residentialAddress.state')}
                          className={`input input-bordered w-full ${getError('residentialAddress.state') ? 'border-red-500' : ''}`}
                        >
                          <option value="">Select State *</option>
                          {INDIAN_STATES.map(state => (
                            <option key={state.code} value={state.name}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                        {getError('residentialAddress.state') && (
                          <p className="text-red-500 text-sm mt-1">
                            {getError('residentialAddress.state')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <input
                          type="text"
                          value={formData.residentialAddress.pincode}
                          onChange={e =>
                            updateAddressField('residentialAddress', 'pincode', e.target.value)
                          }
                          onBlur={() => handleBlur('residentialAddress.pincode')}
                          placeholder="Pincode *"
                          maxLength={6}
                          className={`input input-bordered w-full ${getError('residentialAddress.pincode') ? 'border-red-500' : ''}`}
                        />
                        {getError('residentialAddress.pincode') ? (
                          <p className="text-red-500 text-sm mt-1">
                            {getError('residentialAddress.pincode')}
                          </p>
                        ) : (
                          <p className="text-xs text-text-tertiary mt-1">
                            Enter the 6-digit postal PIN code.
                          </p>
                        )}
                      </div>
                      <input
                        type="text"
                        value={formData.residentialAddress.country}
                        onChange={e =>
                          updateAddressField('residentialAddress', 'country', e.target.value)
                        }
                        placeholder="Country"
                        className="input input-bordered w-full"
                        disabled
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-2 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={formData.permanentAddressSame}
                      onChange={e => updateField('permanentAddressSame', e.target.checked)}
                      className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-text-primary">Permanent address same as residential</span>
                  </label>

                  {!formData.permanentAddressSame && (
                    <div className="space-y-3 bg-surface-overlay/40 rounded-lg p-4 border-l-2 border-primary-500">
                      <h4 className="font-medium text-text-secondary text-sm">Permanent Address</h4>
                      <input
                        type="text"
                        value={formData.permanentAddress.line1}
                        onChange={e =>
                          updateAddressField('permanentAddress', 'line1', e.target.value)
                        }
                        placeholder="Address Line 1"
                        className="input input-bordered w-full"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <input
                          type="text"
                          value={formData.permanentAddress.city}
                          onChange={e =>
                            updateAddressField('permanentAddress', 'city', e.target.value)
                          }
                          placeholder="City"
                          className="input input-bordered w-full"
                        />
                        <select
                          value={formData.permanentAddress.state}
                          onChange={e =>
                            updateAddressField('permanentAddress', 'state', e.target.value)
                          }
                          className="input input-bordered w-full"
                        >
                          <option value="">Select State</option>
                          {INDIAN_STATES.map(state => (
                            <option key={state.code} value={state.name}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="text"
                        value={formData.permanentAddress.pincode}
                        onChange={e =>
                          updateAddressField('permanentAddress', 'pincode', e.target.value)
                        }
                        placeholder="Pincode"
                        maxLength={6}
                        className="input input-bordered w-full max-w-xs"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Financial Profile */}
            {step === 4 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Occupation <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.occupation}
                    onChange={e => updateField('occupation', e.target.value as Occupation)}
                    className="input input-bordered w-full"
                  >
                    <option value="salaried">Salaried</option>
                    <option value="self_employed">Self Employed / Professional</option>
                    <option value="business">Business Owner</option>
                    <option value="retired">Retired</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {(formData.occupation === 'salaried' ||
                  formData.occupation === 'self_employed') && (
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      {formData.occupation === 'salaried'
                        ? 'Employer Name'
                        : 'Business/Practice Name'}
                    </label>
                    <input
                      type="text"
                      value={formData.employerName}
                      onChange={e => updateField('employerName', e.target.value)}
                      placeholder={
                        formData.occupation === 'salaried'
                          ? 'Company name'
                          : 'Business/Practice name'
                      }
                      className="input input-bordered w-full"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Annual Income Range <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.annualIncomeRange}
                    onChange={e => updateField('annualIncomeRange', e.target.value as IncomeRange)}
                    className="input input-bordered w-full"
                  >
                    <option value="0-5L">Below 5 Lakhs</option>
                    <option value="5-10L">5 - 10 Lakhs</option>
                    <option value="10-25L">10 - 25 Lakhs</option>
                    <option value="25-50L">25 - 50 Lakhs</option>
                    <option value="50L+">Above 50 Lakhs</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-medium text-text-primary">
                    Income Sources
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasHouseProperty}
                      onChange={e => updateField('hasHouseProperty', e.target.checked)}
                      className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-text-primary">Has House Property Income</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasCapitalGains}
                      onChange={e => updateField('hasCapitalGains', e.target.checked)}
                      className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-text-primary">
                      Has Capital Gains (Stocks, Property, etc.)
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.hasBusinessIncome}
                      onChange={e => updateField('hasBusinessIncome', e.target.checked)}
                      className="w-4 h-4 rounded border-surface-overlay text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-text-primary">Has Business/Professional Income</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Previous Year Tax Paid
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary">
                      ₹
                    </span>
                    <input
                      type="number"
                      value={formData.previousYearTaxPaid}
                      onChange={e => updateField('previousYearTaxPaid', e.target.value)}
                      placeholder="0"
                      className="input input-bordered w-full pl-8"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Review */}
            {step === 6 && (
              <div className="space-y-6">
                <div className="card card-elevated p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-text-primary">Basic Information</h4>
                    <button onClick={() => setStep(1)} className="btn btn-ghost btn-sm">
                      Edit
                    </button>
                  </div>
                  <div className="text-sm space-y-1 text-text-secondary">
                    <p>
                      <strong>Name:</strong> {formData.fullName}
                    </p>
                    <p>
                      <strong>PAN:</strong>{' '}
                      <code className="bg-surface-overlay px-1 rounded">{formData.pan}</code>
                    </p>
                    <p>
                      <strong>DOB:</strong> {formData.dateOfBirth}
                    </p>
                    <p>
                      <strong>Gender:</strong> {formData.gender}
                    </p>
                  </div>
                </div>

                <div className="card card-elevated p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-text-primary">Contact Information</h4>
                    <button onClick={() => setStep(2)} className="btn btn-ghost btn-sm">
                      Edit
                    </button>
                  </div>
                  <div className="text-sm space-y-1 text-text-secondary">
                    <p>
                      <strong>Email:</strong> {formData.emailPrimary}
                    </p>
                    <p>
                      <strong>Mobile:</strong> {formData.mobilePrimary}
                    </p>
                    {formData.whatsappNumber && (
                      <p>
                        <strong>WhatsApp:</strong> {formData.whatsappNumber}
                      </p>
                    )}
                  </div>
                </div>

                <div className="card card-elevated p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-text-primary">Address</h4>
                    <button onClick={() => setStep(3)} className="btn btn-ghost btn-sm">
                      Edit
                    </button>
                  </div>
                  <div className="text-sm text-text-secondary">
                    <p>{formData.residentialAddress.line1}</p>
                    {formData.residentialAddress.line2 && (
                      <p>{formData.residentialAddress.line2}</p>
                    )}
                    <p>
                      {formData.residentialAddress.city}, {formData.residentialAddress.state} -{' '}
                      {formData.residentialAddress.pincode}
                    </p>
                  </div>
                </div>

                <div className="card card-elevated p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-text-primary">Financial Profile</h4>
                    <button onClick={() => setStep(4)} className="btn btn-ghost btn-sm">
                      Edit
                    </button>
                  </div>
                  <div className="text-sm space-y-1 text-text-secondary">
                    <p>
                      <strong>Occupation:</strong> {formData.occupation.replace('_', ' ')}
                    </p>
                    {formData.employerName && (
                      <p>
                        <strong>Employer:</strong> {formData.employerName}
                      </p>
                    )}
                    <p>
                      <strong>Income Range:</strong> ₹{formData.annualIncomeRange}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.hasHouseProperty && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-xs rounded">
                          House Property
                        </span>
                      )}
                      {formData.hasCapitalGains && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-xs rounded">
                          Capital Gains
                        </span>
                      )}
                      {formData.hasBusinessIncome && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs rounded">
                          Business Income
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-surface-overlay">
          <button
            onClick={step === 1 ? handleClose : handleBack}
            className="btn btn-ghost flex items-center gap-1"
          >
            {step === 1 ? (
              'Cancel'
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" /> Back
              </>
            )}
          </button>

          {step < 6 ? (
            <button onClick={handleNext} className="btn btn-primary flex items-center gap-1">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn btn-primary flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-sm" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  {isEditMode ? 'Update Customer' : 'Create Customer'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
